import { familyPlanSchemaVersion, validateFamilyPlanSnapshot } from "@safesphere/contracts";
import type { FamilyPlanSnapshot } from "@safesphere/contracts";

import { base64UrlToBytes, bytesToBase64Url } from "../offline/encoding.js";
import { getLocalPreference, setLocalPreference } from "../offline/database.js";
import { loadProtectedRecords, loadQuickPlan, replacePlanData } from "./familyRepository.js";
import type { QuickPlanData } from "./familyRepository.js";
import type { EncryptedPayload } from "./storageTypes.js";
import {
  createVaultMaterial,
  decryptBytes,
  deriveVaultKey,
  encryptBytes,
  encryptVaultRecord,
  vaultKdfIterations,
} from "./vaultCrypto.js";

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const backupContext = "SAFESPHERE_FAMILY_BACKUP_V1";
const backupMetadataKey = "family.backup-metadata.v1";

export interface FamilyBackupMetadata {
  readonly exportedAt: string;
  readonly formatVersion: 1;
  readonly planId: string;
}

export async function loadBackupMetadata(): Promise<FamilyBackupMetadata | undefined> {
  return (await getLocalPreference(backupMetadataKey)) as FamilyBackupMetadata | undefined;
}

export interface EncryptedFamilyBackup {
  readonly format: "SAFESPHERE_ENCRYPTED_FAMILY_PLAN";
  readonly formatVersion: 1;
  readonly kdf: {
    readonly algorithm: "PBKDF2-SHA256";
    readonly iterations: number;
    readonly salt: string;
  };
  readonly encryption: EncryptedPayload;
}

interface BackupPayloadV1 {
  readonly schemaVersion: 1;
  readonly snapshot: FamilyPlanSnapshot;
}

interface BackupPayloadV0 {
  readonly schemaVersion: 0;
  readonly planLabel: string;
  readonly snapshot: Omit<FamilyPlanSnapshot, "schemaVersion" | "plan"> & {
    readonly plan: Omit<FamilyPlanSnapshot["plan"], "label" | "schemaVersion">;
  };
}

export interface RecoveryPreview {
  readonly snapshot: FamilyPlanSnapshot;
  readonly migratedFrom?: number;
  readonly counts: {
    readonly contacts: number;
    readonly meetingPoints: number;
    readonly checklists: number;
    readonly protectedRecords: number;
  };
}

export type BackupPreviewResult =
  | { readonly ok: true; readonly preview: RecoveryPreview }
  | {
      readonly ok: false;
      readonly reason: "MALFORMED" | "INCOMPATIBLE" | "INCORRECT_PASSPHRASE" | "INVALID_DATA";
    };

function isEnvelope(value: unknown): value is EncryptedFamilyBackup {
  if (typeof value !== "object" || value === null) return false;
  const envelope = value as Partial<EncryptedFamilyBackup>;
  return (
    envelope.format === "SAFESPHERE_ENCRYPTED_FAMILY_PLAN" &&
    envelope.formatVersion === 1 &&
    envelope.kdf?.algorithm === "PBKDF2-SHA256" &&
    typeof envelope.kdf.iterations === "number" &&
    typeof envelope.kdf.salt === "string" &&
    envelope.encryption?.algorithm === "AES-GCM" &&
    typeof envelope.encryption.iv === "string" &&
    typeof envelope.encryption.ciphertext === "string"
  );
}

function migratePayload(
  value: unknown,
): { snapshot: FamilyPlanSnapshot; migratedFrom?: number } | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const version = (value as { readonly schemaVersion?: unknown }).schemaVersion;
  if (version === 1) return { snapshot: (value as BackupPayloadV1).snapshot };
  if (version === 0) {
    const legacy = value as BackupPayloadV0;
    return {
      migratedFrom: 0,
      snapshot: {
        ...legacy.snapshot,
        schemaVersion: familyPlanSchemaVersion,
        plan: {
          ...legacy.snapshot.plan,
          label: legacy.planLabel,
          schemaVersion: familyPlanSchemaVersion,
        },
      },
    };
  }
  return undefined;
}

export async function encryptBackupPayload(
  snapshot: FamilyPlanSnapshot,
  passphrase: string,
  payloadSchemaVersion: 0 | 1 = 1,
): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await deriveVaultKey(passphrase, salt);
  const payload: BackupPayloadV1 | BackupPayloadV0 =
    payloadSchemaVersion === 1
      ? { schemaVersion: 1, snapshot }
      : {
          schemaVersion: 0,
          planLabel: snapshot.plan.label,
          snapshot: {
            plan: {
              id: snapshot.plan.id,
              fictionalFixture: snapshot.plan.fictionalFixture,
              updatedAt: snapshot.plan.updatedAt,
            },
            contacts: snapshot.contacts,
            meetingPoints: snapshot.meetingPoints,
            checklists: snapshot.checklists,
            protectedRecords: snapshot.protectedRecords,
          },
        };
  const envelope: EncryptedFamilyBackup = {
    format: "SAFESPHERE_ENCRYPTED_FAMILY_PLAN",
    formatVersion: 1,
    kdf: {
      algorithm: "PBKDF2-SHA256",
      iterations: vaultKdfIterations,
      salt: bytesToBase64Url(salt),
    },
    encryption: await encryptBytes(encoder.encode(JSON.stringify(payload)), key, backupContext),
  };
  return JSON.stringify(envelope);
}

export async function exportFamilyPlan(planId: string, passphrase: string): Promise<string> {
  const quick = await loadQuickPlan(planId);
  if (quick === undefined) throw new Error("PLAN_NOT_FOUND");
  const protectedRecords = await loadProtectedRecords(planId);
  const serialized = await encryptBackupPayload(
    { schemaVersion: 1, ...quick, protectedRecords },
    passphrase,
  );
  await setLocalPreference(backupMetadataKey, {
    exportedAt: new Date().toISOString(),
    formatVersion: 1,
    planId,
  });
  return serialized;
}

export async function previewFamilyPlanImport(
  serialized: string,
  passphrase: string,
): Promise<BackupPreviewResult> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized) as unknown;
  } catch {
    return { ok: false, reason: "MALFORMED" };
  }
  if (!isEnvelope(parsed)) return { ok: false, reason: "INCOMPATIBLE" };
  let value: unknown;
  try {
    const key = await deriveVaultKey(
      passphrase,
      base64UrlToBytes(parsed.kdf.salt),
      parsed.kdf.iterations,
    );
    value = JSON.parse(
      decoder.decode(await decryptBytes(parsed.encryption, key, backupContext)),
    ) as unknown;
  } catch {
    return { ok: false, reason: "INCORRECT_PASSPHRASE" };
  }
  const migrated = migratePayload(value);
  if (migrated === undefined) return { ok: false, reason: "INCOMPATIBLE" };
  const validation = validateFamilyPlanSnapshot(migrated.snapshot);
  if (!validation.valid) return { ok: false, reason: "INVALID_DATA" };
  return {
    ok: true,
    preview: {
      ...migrated,
      counts: {
        contacts: migrated.snapshot.contacts.length,
        meetingPoints: migrated.snapshot.meetingPoints.length,
        checklists: migrated.snapshot.checklists.length,
        protectedRecords: migrated.snapshot.protectedRecords.length,
      },
    },
  };
}

export async function commitFamilyPlanImport(
  preview: RecoveryPreview,
  passphrase: string,
): Promise<void> {
  const { snapshot } = preview;
  const material = await createVaultMaterial(snapshot.plan.id, passphrase);
  const encryptedRecords = await Promise.all(
    snapshot.protectedRecords.map((record) => encryptVaultRecord(record, material.key)),
  );
  const quick: QuickPlanData = {
    plan: snapshot.plan,
    contacts: snapshot.contacts,
    meetingPoints: snapshot.meetingPoints,
    checklists: snapshot.checklists,
  };
  await replacePlanData(quick, material.metadata, encryptedRecords, material.key);
}
